import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  ChartBarIcon, UsersIcon, ShoppingBagIcon, BuildingStorefrontIcon,
  CreditCardIcon, ArrowUturnLeftIcon, ChatBubbleLeftRightIcon,
  BellIcon, TagIcon, Squares2X2Icon, ShieldCheckIcon, Cog6ToothIcon,
  ArrowRightOnRectangleIcon, Bars3Icon, XMarkIcon, ChevronDownIcon,
  DocumentTextIcon, CubeIcon, StarIcon,
} from '@heroicons/react/24/outline';
import { toggleSidebar, setMobileSidebar } from '../../store/slices/uiSlice';
import { logoutThunk, selectUser } from '../../store/slices/authSlice';
import { Avatar } from '../common/UI';
import logoImg from '../../assets/logo.png';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { to: '/admin',              label: 'Dashboard',         icon: ChartBarIcon, exact: true },
    ],
  },
  {
    label: 'Merchants & Catalog',
    items: [
      { to: '/admin/sellers',      label: 'Sellers & KYC',     icon: BuildingStorefrontIcon },
      { to: '/admin/catalog',      label: 'Product Moderation',icon: CubeIcon },
      { to: '/admin/categories',   label: 'Categories',        icon: TagIcon },
    ],
  },
  {
    label: 'Commerce & Stock',
    items: [
      { to: '/admin/orders',       label: 'Master Orders',     icon: ShoppingBagIcon },
      { to: '/admin/inventory',    label: 'Inventory Gov.',    icon: Squares2X2Icon },
      { to: '/admin/finance',      label: 'Finance & Ledger',  icon: CreditCardIcon },
    ],
  },
  {
    label: 'Users & Access',
    items: [
      { to: '/admin/customers',    label: 'Customers',         icon: UsersIcon },
      { to: '/admin/admin-users',  label: 'Admin Staff & RBAC',icon: ShieldCheckIcon },
    ],
  },
  {
    label: 'Marketing & Support',
    items: [
      { to: '/admin/marketing',    label: 'Coupons & Banners', icon: TagIcon },
      { to: '/admin/reviews',      label: 'Reviews Moderation',icon: StarIcon },
      { to: '/admin/inquiry',      label: 'Inquiries',         icon: DocumentTextIcon },
      { to: '/admin/support',      label: 'Support Desk',      icon: ChatBubbleLeftRightIcon },
    ],
  },
  {
    label: 'System & Security',
    items: [
      { to: '/admin/security',     label: 'Security Center',   icon: ShieldCheckIcon },
      { to: '/admin/settings',     label: 'System Settings',   icon: Cog6ToothIcon },
    ],
  },
];

// ── Sidebar link ──────────────────────────────────────
const SideLink = ({ to, icon: Icon, label, exact, onClick }) => (
  <NavLink
    to={to}
    end={exact}
    onClick={onClick}
    className={({ isActive }) => clsx('nav-link', isActive && 'active')}
  >
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span>{label}</span>
  </NavLink>
);

// ── Sidebar content ───────────────────────────────────
const SidebarContent = ({ onLinkClick }) => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const user      = useSelector(selectUser);

  const handleLogout = async () => {
    await dispatch(logoutThunk());
    navigate('/login');
  };

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-surface-100">
        <img src={logoImg} alt="Bellezbuy Admin" className="h-9 w-9 object-contain" />
        <div>
          <span className="font-display font-bold text-sm text-surface-900 leading-tight block">Bellezbuy</span>
          <p className="text-[10px] font-semibold text-primary-600 uppercase tracking-widest">Admin Portal</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5 scrollbar-thin">
        {NAV_GROUPS.map(group => (
          <div key={group.label}>
            <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-widest text-surface-400">{group.label}</p>
            <div className="space-y-0.5">
              {group.items.map(item => (
                <SideLink key={item.to} {...item} onClick={onLinkClick} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div className="border-t border-surface-100 p-3">
        <div className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-surface-50 transition-colors">
          <Avatar name={`${user?.first_name} ${user?.last_name}`} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="truncate text-xs font-semibold text-surface-800">{user?.first_name} {user?.last_name}</p>
            <p className="truncate text-[10px] text-surface-400 capitalize">{user?.role?.replace(/_/g,' ')}</p>
          </div>
          <button onClick={handleLogout} className="p-1 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-danger-600 transition-colors" title="Logout">
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Admin Shell ───────────────────────────────────────
export const AdminShell = ({ children }) => {
  const dispatch     = useDispatch();
  const sidebarOpen  = useSelector(s => s.ui.sidebarOpen);
  const mobileSidebar= useSelector(s => s.ui.mobileSidebar);
  const user         = useSelector(selectUser);

  return (
    <div className="flex h-screen overflow-hidden bg-surface-50">
      {/* Desktop sidebar */}
      <aside className={clsx(
        'hidden lg:flex flex-col flex-shrink-0 bg-white border-r border-surface-100 transition-all duration-300 overflow-hidden',
        sidebarOpen ? 'w-[260px]' : 'w-0'
      )}>
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileSidebar && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => dispatch(setMobileSidebar(false))} />
          <aside className="absolute left-0 top-0 h-full w-[260px] bg-white shadow-2xl">
            <SidebarContent onLinkClick={() => dispatch(setMobileSidebar(false))} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-surface-100 bg-white px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => dispatch(toggleSidebar())} className="hidden lg:flex btn btn-ghost p-2 rounded-xl">
              <Bars3Icon className="h-5 w-5" />
            </button>
            <button onClick={() => dispatch(setMobileSidebar(true))} className="lg:hidden btn btn-ghost p-2 rounded-xl">
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-surface-100">
              <Avatar name={`${user?.first_name} ${user?.last_name}`} size="sm" />
              <div className="hidden md:block">
                <p className="text-xs font-semibold text-surface-800">{user?.first_name}</p>
                <p className="text-[10px] text-surface-400 capitalize">{user?.role?.replace(/_/g,' ')}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 lg:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

// ── Notification bell ─────────────────────────────────
const NotificationBell = () => {
  const [count] = React.useState(3);
  return (
    <button className="relative btn btn-ghost p-2 rounded-xl">
      <BellIcon className="h-5 w-5" />
      {count > 0 && (
        <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-danger-500 text-[9px] font-bold text-white">
          {count}
        </span>
      )}
    </button>
  );
};

export default AdminShell;
